/**
 * Helpers for `<input type="date">`, whose value is always a plain `YYYY-MM-DD`
 * string with no timezone attached.
 */

/** Calendar day of `date` in the user's timezone, formatted for a date input. */
export function toDateInputValue(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function todayDateInputValue(now: Date = new Date()): string {
  return toDateInputValue(now)
}

/**
 * `new Date('2026-08-04')` is parsed as UTC midnight, which resolves to the previous
 * day in negative offsets such as America/Sao_Paulo. Appending a time component
 * forces local parsing, so the stored instant matches the day the user picked.
 */
export function parseDateInputValue(value: string): Date | null {
  if (!value) return null
  const date = new Date(`${value}T00:00:00`)
  return Number.isNaN(date.getTime()) ? null : date
}

/** ISO timestamp at local midnight of the picked day, ready to be persisted. */
export function dateInputToIso(value: string): string | undefined {
  return parseDateInputValue(value)?.toISOString()
}

/** Local calendar day of a stored timestamp, ready to fill a date input. */
export function isoToDateInputValue(iso?: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? '' : toDateInputValue(date)
}

/** `YYYY-MM-DD` sorts lexicographically, so a string compare is enough here. */
export function isFutureDateInputValue(value: string, now: Date = new Date()): boolean {
  return Boolean(value) && value > toDateInputValue(now)
}
